import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface UserStats {
  monthlyStreams: number;
  monthlyStreamsChange: number;
  totalStreams: number;
  followers: number;
  followersChange: number;
  following: number;
  releasedTracks: number;
  releasedTracksChange: number;
  healthScore: number;
  healthScoreChange: number;
  lastCalculatedAt: Date;
}

export const useUserStats = () => {
  return useQuery({
    queryKey: ['user-stats'],
    queryFn: async () => {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      if (!user) throw new Error('No authenticated user');

      // Fetch user stats
      const { data, error } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      // If no stats exist yet, return defaults
      if (!data) {
        return {
          monthlyStreams: 0,
          monthlyStreamsChange: 0,
          totalStreams: 0,
          followers: 0,
          followersChange: 0,
          following: 0,
          releasedTracks: 0,
          releasedTracksChange: 0,
          healthScore: 50,
          healthScoreChange: 0,
          lastCalculatedAt: new Date(),
        } as UserStats;
      }

      // Map database fields to UserStats interface
      return {
        monthlyStreams: data.monthly_streams || 0,
        monthlyStreamsChange: data.monthly_streams_change || 0,
        totalStreams: data.total_streams || 0,
        followers: data.followers || 0,
        followersChange: data.followers_change || 0,
        following: data.following || 0,
        releasedTracks: data.released_tracks || 0,
        releasedTracksChange: data.released_tracks_change || 0,
        healthScore: data.health_score || 50,
        healthScoreChange: data.health_score_change || 0,
        lastCalculatedAt: new Date(data.last_calculated_at),
      } as UserStats;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};
